/**
 * Project Store
 *
 * Manages project state, file tree, and open files.
 */
import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import type { Project, FileNode, OpenFile } from '@/types';
import { api, ApiError } from '@/services/api';

// 2026-06-05 active-project-id persistence. User feedback: "we need to be
// able to save the state and progress in the game - so clicking the reload
// button dont reset everything." Briefing-room's drawer/lastRoll persistence
// shipped at 1.270.0 — this answers the second leg by remembering which
// project the user was last viewing. We persist ONLY the id; the full
// Project object is re-fetched via fetchProjects on every mount.
export const ACTIVE_PROJECT_KEY = 'vibe-designer:active-project-id';

export function readActiveProjectId(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(ACTIVE_PROJECT_KEY);
  } catch {
    return null;
  }
}

export function writeActiveProjectId(id: string | null): void {
  if (typeof window === 'undefined') return;
  try {
    if (id === null) {
      window.localStorage.removeItem(ACTIVE_PROJECT_KEY);
    } else {
      window.localStorage.setItem(ACTIVE_PROJECT_KEY, id);
    }
  } catch {
    // Quota / privacy mode — degrade silently. Same pattern as
    // briefing-room's writeScenarioState.
  }
}

interface ProjectListItem {
  id: string;
  name: string;
  updated_at: string;
  // Project-lifecycle (2026-07-28): last-opened stamp (shown as "Last viewed
  // {date}" in the switcher) + soft-archive flag ("frozen" / IceBox).
  last_viewed_at?: string;
  archived?: boolean;
}

interface ProjectListResponse {
  projects: ProjectListItem[];
  total: number;
}

interface FileTreeResponse {
  files: FileNode;
}

interface ProjectState {
  // Current project
  currentProject: Project | null;
  // E.21.35 — full list of projects for the multi-project switcher dropdown
  projects: ProjectListItem[];
  isLoading: boolean;
  error: string | null;

  // File tree
  fileTree: FileNode | null;
  expandedFolders: Set<string>;

  // Open files
  openFiles: OpenFile[];
  activeFileId: string | null;

  // Actions - Project
  setCurrentProject: (project: Project | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;

  // E.21.28 — backend-wired project lifecycle
  fetchProjects: () => Promise<void>;
  fetchTree: (projectId: string) => Promise<void>;
  // Issue 5 (2026-07-14) — background stale-check. GET /projects/{id} returns
  // + consumes the one-shot `forgejo_cache_stale` sentinel the Forgejo webhook
  // writes on a git push; when true we refetch the tree. Best-effort: never
  // surfaces an error banner (it is a silent poll). Complemented by an
  // unconditional foreground refresh for non-push volume writes (see
  // useProjectTreeAutoRefresh).
  refreshTreeIfStale: (projectId: string) => Promise<void>;
  ensureProject: () => Promise<void>;

  // E.21.29 — create new project (FilesPanel + New Project button)
  createProject: (name: string, template?: string) => Promise<void>;

  // E.21.35 — multi-project switcher (TopBar dropdown)
  switchProject: (projectId: string) => Promise<void>;

  // Actions - File Tree
  setFileTree: (tree: FileNode) => void;
  setExpandedFolders: (folders: Set<string>) => void;
  toggleFolder: (folderId: string) => void;
  expandFolder: (folderId: string) => void;
  collapseFolder: (folderId: string) => void;

  // Actions - Open Files
  openFile: (file: OpenFile) => void;
  closeFile: (fileId: string) => void;
  closeAllFiles: () => void;
  setActiveFile: (fileId: string | null) => void;
  updateFileContent: (fileId: string, content: string) => void;
  markFileSaved: (fileId: string) => void;
  updateCursorPosition: (fileId: string, line: number, column: number) => void;

  // 2026-08-17 empty-editor fix — resolve a buffer opened WITHOUT tree-inlined
  // content (contentSource 'unfetched') once GET /api/files/{id}/read answers.
  setFileContentLoaded: (fileId: string, content: string, language?: string) => void;
  setFileContentError: (fileId: string, message: string) => void;

  // Phase A — persist edits to disk via PUT /api/projects/{id}/files/{path}
  saveFile: (fileId: string) => Promise<void>;

  // 2026-05-06: arbitrary-path write used by the AI Chat "Apply to project"
  // button on `file:<path>` code blocks. PUT is upsert (creates parents +
  // file if missing). Refreshes file tree so the new file appears in the
  // Explorer immediately.
  writeFile: (path: string, content: string) => Promise<void>;

  // 2026-06-29 — bulk upload for the Explorer uploader (multi-file picker,
  // recursive folder picker, drag-drop). Upserts each file via the same PUT
  // writeFile uses (creates parent dirs), then refetches the tree ONCE after
  // the batch. Best-effort: never throws; returns per-file failures so the UI
  // can report partial success.
  uploadFiles: (
    projectId: string,
    files: Array<{ path: string; content: string }>,
  ) => Promise<{ ok: boolean; written: number; failed: Array<{ path: string; error: string }> }>;

  // 2026-07-11 — binary sibling of uploadFiles. Text uploads store a UTF-8
  // string via PUT (Forgejo-synced); a binary (zip/png/mp4) can't survive that,
  // so it POSTs raw bytes to /files/{id}/upload (multipart, workspace-only).
  // Same best-effort contract (never throws; per-file failures) + single
  // fetchTree after the batch.
  uploadBinaryFiles: (
    projectId: string,
    files: Array<{ path: string; file: File }>,
  ) => Promise<{ ok: boolean; written: number; failed: Array<{ path: string; error: string }> }>;

  // W2 (2026-05-25) — FilesPanel sidebar "add file" + "add folder" buttons.
  // Posts to existing endpoint POST /files/{projectId}/create with
  // FileCreate(path, content="", type=...). Returns {ok, error?} so the
  // caller can render inline error UI on 409/4xx without throwing.
  createFile: (projectId: string, path: string, type: 'file' | 'directory') => Promise<{ ok: boolean; error?: string }>;

  // 2026-06-27 navigator + project-mgmt overhaul — wire the EXISTING backend
  // endpoints the UI never called. All return {ok,error?} (mirroring
  // createFile) so callers render inline error UI; NEVER throw on 4xx.
  //   renameFile    → POST   /files/{id}/rename   {oldPath,newPath} (also moves)
  //   deleteFile    → DELETE /files/{id}/delete?path=...
  //   renameProject → PUT    /projects/{id}       {name}
  //   deleteProject → DELETE /projects/{id}        (204)
  renameFile: (projectId: string, oldPath: string, newPath: string) => Promise<{ ok: boolean; error?: string }>;
  deleteFile: (projectId: string, path: string) => Promise<{ ok: boolean; error?: string }>;
  renameProject: (projectId: string, name: string) => Promise<{ ok: boolean; error?: string }>;
  deleteProject: (projectId: string) => Promise<{ ok: boolean; error?: string }>;

  // Phase 4 (2026-06-27): duplicate (deep-copy → new project, switch to it) +
  // archive (soft-hide; project stays on disk, drops out of the default list).
  duplicateProject: (projectId: string) => Promise<{ ok: boolean; error?: string }>;
  archiveProject: (projectId: string) => Promise<{ ok: boolean; error?: string }>;
  // Project-lifecycle (2026-07-28): IceBox restore + permanent purge.
  //   unarchiveProject → PUT    /projects/{id} {archived:false}   (Unfreeze)
  //   purgeProject     → DELETE /projects/{id}?purge=true         (Delete permanently)
  unarchiveProject: (projectId: string) => Promise<{ ok: boolean; error?: string }>;
  purgeProject: (projectId: string) => Promise<{ ok: boolean; error?: string }>;

  // Getters
  getActiveFile: () => OpenFile | undefined;
  getFileById: (fileId: string) => OpenFile | undefined;
  hasUnsavedChanges: () => boolean;
}

export const useProjectStore = create<ProjectState>()(
  immer((set, get) => ({
    // Initial state
    currentProject: null,
    projects: [], // E.21.35
    isLoading: false,
    error: null,
    fileTree: null,
    expandedFolders: new Set<string>(),
    openFiles: [],
    activeFileId: null,

    // Project actions
    setCurrentProject: (project) =>
      set((state) => {
        state.currentProject = project;
        state.fileTree = project?.files ?? null;
      }),

    setLoading: (loading) =>
      set((state) => {
        state.isLoading = loading;
      }),

    setError: (error) =>
      set((state) => {
        state.error = error;
      }),

    fetchProjects: async () => {
      // E.21.35 (reviewer N3): try/catch that mirrors fetchTree. Pre-fix
      // a 5xx from /api/projects propagated uncaught — switchProject's
      // post-switch refresh would silently leave the store inconsistent.
      try {
        const data = await api.get<ProjectListResponse>('/projects');
        set((state) => {
          state.projects = data.projects;
          // Preserve E.21.28 contract: if no currentProject set, prefer
          // the persisted active-project-id when it points at a project
          // still in the list (2026-06-05). Falls back to data.projects[0]
          // (sorted-desc by backend = most-recent) when persisted id is
          // stale or absent.
          if (!state.currentProject) {
            const persistedId = readActiveProjectId();
            const persistedMatch = persistedId
              ? data.projects.find((p) => p.id === persistedId)
              : undefined;
            const chosen = persistedMatch ?? data.projects[0];
            state.currentProject = chosen ? (chosen as unknown as Project) : null;
          }
          state.error = null;
        });
      } catch (e) {
        const msg = e instanceof ApiError ? e.message : String(e);
        set((state) => {
          state.error = msg;
        });
      }
    },

    fetchTree: async (projectId) => {
      try {
        const data = await api.get<FileTreeResponse>(`/projects/${projectId}/files`);
        set((state) => {
          state.fileTree = data.files;
          state.error = null;
        });
      } catch (e) {
        const msg = e instanceof ApiError ? e.message : String(e);
        set((state) => {
          state.error = msg;
        });
      }
    },

    refreshTreeIfStale: async (projectId) => {
      try {
        // The raw GET /projects/{id} payload carries forgejo_cache_stale, which
        // is not on the Project TS interface (it is a transient response-only
        // flag), so read it off a narrow local shape.
        const meta = await api.get<{ forgejo_cache_stale?: boolean }>(
          `/projects/${projectId}`,
        );
        if (meta.forgejo_cache_stale) {
          await get().fetchTree(projectId);
        }
      } catch {
        // Best-effort background poll — swallow (do NOT set state.error, that
        // would flash the Explorer error banner on a transient network blip).
      }
    },

    ensureProject: async () => {
      const list = await api.get<ProjectListResponse>('/projects');
      let project: ProjectListItem;
      let allProjects = list.projects;
      if (allProjects.length > 0) {
        // 2026-06-05: same persistence preference as fetchProjects.
        // Honor the active-project-id when its project is still in
        // the list; otherwise fall back to projects[0] (most-recent).
        const persistedId = readActiveProjectId();
        const persistedMatch = persistedId
          ? allProjects.find((p) => p.id === persistedId)
          : undefined;
        project = persistedMatch ?? allProjects[0];
      } else {
        project = await api.post<ProjectListItem>('/projects', {
          name: 'My First Project',
          template: 'blank',
        });
        // After creating the first project, the projects list now has 1 item.
        allProjects = [project];
      }
      set((state) => {
        state.currentProject = project as unknown as Project;
        // E.21.35: populate the projects list so the switcher dropdown
        // has the full set (not just currentProject).
        state.projects = allProjects;
      });
      await get().fetchTree(project.id);
    },

    createProject: async (name, template) => {
      // POST creates a new project + scaffolds /workspace/<uuid>/{src,tests,docs,memory,AGENTS.md}
      // per E.21.27 task D. Reviewer N2: backend ProjectCreate.template default is 'blank'.
      const project = await api.post<ProjectListItem>('/projects', {
        name,
        template: template ?? 'blank',
      });
      // Reviewer N1: REPLACES currentProject. Prior project remains
      // on disk in /workspace and is now reachable via the E.21.35
      // switcher dropdown.
      set((state) => {
        state.currentProject = project as unknown as Project;
        // E.21.35: prepend new project to the list (sorted desc by
        // updated_at — newly-created is most recent).
        state.projects = [project, ...state.projects];
      });
      // 2026-06-05: persist so the next reload lands here (matches the
      // "newly created should be active" expectation).
      writeActiveProjectId(project.id);
      await get().fetchTree(project.id);
    },

    switchProject: async (projectId) => {
      // E.21.35: switch currentProject to a project already known in
      // state.projects. The TopBar dropdown calls this with one of the
      // existing project IDs. If projectId isn't in state.projects,
      // log a warning and no-op (reviewer N1 documented behavior).
      const target = get().projects.find((p) => p.id === projectId);
      if (!target) {
        console.warn(`[projectStore] switchProject: unknown id ${projectId}`);
        return;
      }
      if (target.id === get().currentProject?.id) return; // no-op self-switch
      set((state) => {
        state.currentProject = target as unknown as Project;
        // Reset fileTree so old project's tree doesn't flash before
        // fetchTree resolves.
        state.fileTree = null;
        state.openFiles = [];
        state.activeFileId = null;
      });
      // 2026-06-05: persist after the in-list / self-switch guards so
      // a successful switch survives reload. Unknown-id + self-switch
      // both bail before this line so localStorage is not touched.
      writeActiveProjectId(target.id);
      // Project-lifecycle (2026-07-28): stamp "last viewed" on OPEN. Best-effort
      // (never blocks the switch / surfaces a banner) + optimistic local update
      // so the switcher reflects it before the next fetchProjects. This is why
      // the stamp lives on switchProject, NOT the 30s GET /{id} stale-poll.
      const stampedAt = new Date().toISOString();
      set((state) => {
        const entry = state.projects.find((p) => p.id === target.id);
        if (entry) entry.last_viewed_at = stampedAt;
      });
      void api.post(`/projects/${target.id}/viewed`).catch(() => {
        /* best-effort — a failed stamp must not break opening a project */
      });
      await get().fetchTree(target.id);
    },

    // File tree actions
    setFileTree: (tree) =>
      set((state) => {
        state.fileTree = tree;
      }),

    setExpandedFolders: (folders) =>
      set((state) => {
        state.expandedFolders = folders;
      }),

    toggleFolder: (folderId) =>
      set((state) => {
        if (state.expandedFolders.has(folderId)) {
          state.expandedFolders.delete(folderId);
        } else {
          state.expandedFolders.add(folderId);
        }
      }),

    expandFolder: (folderId) =>
      set((state) => {
        state.expandedFolders.add(folderId);
      }),

    collapseFolder: (folderId) =>
      set((state) => {
        state.expandedFolders.delete(folderId);
      }),

    // Open files actions
    openFile: (file) =>
      set((state) => {
        // Check if file is already open
        const existingIndex = state.openFiles.findIndex((f) => f.id === file.id);
        if (existingIndex === -1) {
          state.openFiles.push(file);
        }
        state.activeFileId = file.id;
      }),

    closeFile: (fileId) =>
      set((state) => {
        const index = state.openFiles.findIndex((f) => f.id === fileId);
        if (index !== -1) {
          state.openFiles.splice(index, 1);

          // Update active file if we closed the active one
          if (state.activeFileId === fileId) {
            if (state.openFiles.length > 0) {
              // Activate the previous tab or the first one
              const newIndex = Math.min(index, state.openFiles.length - 1);
              state.activeFileId = state.openFiles[newIndex].id;
            } else {
              state.activeFileId = null;
            }
          }
        }
      }),

    closeAllFiles: () =>
      set((state) => {
        state.openFiles = [];
        state.activeFileId = null;
      }),

    setActiveFile: (fileId) =>
      set((state) => {
        state.activeFileId = fileId;
      }),

    updateFileContent: (fileId, content) =>
      set((state) => {
        const file = state.openFiles.find((f) => f.id === fileId);
        if (file) {
          // G1 (2026-08-17): @monaco-editor/react echoes a programmatic
          // setValue on a readOnly editor through onDidChangeModelContent
          // (its edit-suppression guard only wraps the editable branch), so
          // an 'error' notice buffer would get a spurious isModified=true.
          // These buffers are read-only by design — any change event from
          // them is the library echo, never the user.
          if (file.contentSource === 'unfetched' || file.contentSource === 'error') {
            return;
          }
          file.content = content;
          file.isModified = true;
        }
      }),

    markFileSaved: (fileId) =>
      set((state) => {
        const file = state.openFiles.find((f) => f.id === fileId);
        if (file) {
          file.isModified = false;
        }
      }),

    setFileContentLoaded: (fileId, content, language) =>
      set((state) => {
        const file = state.openFiles.find((f) => f.id === fileId);
        if (file) {
          file.content = content;
          if (language) file.language = language;
          file.contentSource = 'fetched';
          file.isModified = false;
        }
      }),

    setFileContentError: (fileId, message) =>
      set((state) => {
        const file = state.openFiles.find((f) => f.id === fileId);
        if (file) {
          // The buffer text is a NOTICE, not file content — contentSource
          // 'error' keeps it read-only and unsaveable (saveFile guard).
          file.content = `// Unable to display this file here: ${message}\n// The file on disk is untouched. Use Download or a terminal agent to view it.`;
          file.contentSource = 'error';
          file.isModified = false;
        }
      }),

    writeFile: async (path: string, content: string) => {
      const project = get().currentProject;
      if (!project) {
        throw new Error('No active project — open a project before writing files.');
      }
      const normalized = path.startsWith('/') ? path : `/${path}`;
      try {
        await api.put(`/projects/${project.id}/files${normalized}`, { content });
        // Refresh the tree so the Explorer reflects the new/updated file.
        await get().fetchTree(project.id);
        set((s) => {
          s.error = null;
        });
      } catch (e) {
        const msg = e instanceof ApiError ? e.message : String(e);
        set((s) => {
          s.error = msg;
        });
        throw e;
      }
    },

    // 2026-06-29 — bulk upload. Sequential per-file PUT (same upsert endpoint as
    // writeFile, which creates parent dirs), collecting failures, then a SINGLE
    // fetchTree at the end so the Explorer reflects every new node at once.
    uploadFiles: async (projectId, files) => {
      const failed: Array<{ path: string; error: string }> = [];
      let written = 0;
      for (const { path, content } of files) {
        const normalized = path.startsWith('/') ? path : `/${path}`;
        try {
          await api.put(`/projects/${projectId}/files${normalized}`, { content });
          written += 1;
        } catch (e) {
          const msg = e instanceof ApiError ? e.message : String(e);
          failed.push({ path, error: msg });
        }
      }
      try {
        await get().fetchTree(projectId);
      } catch {
        // Tree refetch is best-effort; the writes already succeeded.
      }
      set((s) => {
        s.error = failed.length > 0 ? `${failed.length} file(s) failed to upload` : null;
      });
      return { ok: failed.length === 0, written, failed };
    },

    // 2026-07-11 — binary bulk upload. Each file is POSTed as multipart raw
    // bytes to /files/{id}/upload (via api.postForm, which keeps the Bearer and
    // lets the browser set the multipart boundary). Mirrors uploadFiles'
    // best-effort per-file loop + single fetchTree. Binaries are workspace-only
    // (no Forgejo commit).
    uploadBinaryFiles: async (projectId, files) => {
      const failed: Array<{ path: string; error: string }> = [];
      let written = 0;
      for (const { path, file } of files) {
        const rel = path.replace(/^\/+/, '');
        try {
          const form = new FormData();
          form.append('path', rel);
          form.append('file', file, file.name);
          await api.postForm(`/files/${projectId}/upload`, form);
          written += 1;
        } catch (e) {
          const msg = e instanceof ApiError ? e.message : String(e);
          failed.push({ path, error: msg });
        }
      }
      try {
        await get().fetchTree(projectId);
      } catch {
        // Tree refetch is best-effort; the uploads already succeeded.
      }
      set((s) => {
        s.error = failed.length > 0 ? `${failed.length} file(s) failed to upload` : null;
      });
      return { ok: failed.length === 0, written, failed };
    },

    // W2 (2026-05-25) — file/folder creation for FilesPanel sidebar buttons.
    // POSTs to existing backend endpoint at routes/files.py:438. Does NOT
    // throw on 4xx/409 — returns {ok:false, error} so the caller renders
    // an inline error in the new-file input (D-W2-3 LOCKED). On success
    // refetches the tree so the new node appears in the explorer.
    createFile: async (projectId: string, path: string, type: 'file' | 'directory') => {
      try {
        await api.post(`/files/${projectId}/create`, { path, content: '', type });
        await get().fetchTree(projectId);
        return { ok: true };
      } catch (e) {
        const msg = e instanceof ApiError ? e.message : String(e);
        return { ok: false, error: msg };
      }
    },

    // 2026-06-27 navigator + project-mgmt overhaul. Same best-effort contract
    // as createFile: {ok,error?}, no throw on 4xx. renameFile/deleteFile refetch
    // the tree on success so the Explorer reflects the change immediately.
    renameFile: async (projectId, oldPath, newPath) => {
      try {
        await api.post(`/files/${projectId}/rename`, { oldPath, newPath });
        await get().fetchTree(projectId);
        return { ok: true };
      } catch (e) {
        const msg = e instanceof ApiError ? e.message : String(e);
        return { ok: false, error: msg };
      }
    },

    deleteFile: async (projectId, path) => {
      try {
        // Backend reads `path` as a QUERY param (routes/files.py:537), not body.
        await api.delete(`/files/${projectId}/delete`, { params: { path } });
        await get().fetchTree(projectId);
        return { ok: true };
      } catch (e) {
        const msg = e instanceof ApiError ? e.message : String(e);
        return { ok: false, error: msg };
      }
    },

    renameProject: async (projectId, name) => {
      try {
        await api.put(`/projects/${projectId}`, { name });
        set((state) => {
          const entry = state.projects.find((p) => p.id === projectId);
          if (entry) entry.name = name;
          if (state.currentProject && state.currentProject.id === projectId) {
            state.currentProject.name = name;
          }
          state.error = null;
        });
        return { ok: true };
      } catch (e) {
        const msg = e instanceof ApiError ? e.message : String(e);
        return { ok: false, error: msg };
      }
    },

    deleteProject: async (projectId) => {
      try {
        await api.delete(`/projects/${projectId}`); // 204
      } catch (e) {
        const msg = e instanceof ApiError ? e.message : String(e);
        return { ok: false, error: msg };
      }
      // Compute the next current BEFORE mutating so we can fetch its tree after.
      const wasCurrent = get().currentProject?.id === projectId;
      const remaining = get().projects.filter((p) => p.id !== projectId);
      const next = wasCurrent ? (remaining[0] ?? null) : null;
      set((state) => {
        state.projects = remaining;
        if (wasCurrent) {
          state.currentProject = next ? (next as unknown as Project) : null;
          // Reset view state so the deleted project's tree/tabs don't linger.
          state.fileTree = null;
          state.openFiles = [];
          state.activeFileId = null;
        }
        state.error = null;
      });
      if (wasCurrent) {
        // Keep the resume key consistent (matches switchProject/createProject).
        writeActiveProjectId(next?.id ?? null);
        if (next) await get().fetchTree(next.id);
      }
      return { ok: true };
    },

    duplicateProject: async (projectId) => {
      try {
        // Backend POST /projects/{id}/duplicate returns the copy's metadata.
        const copy = await api.post<ProjectListItem>(`/projects/${projectId}/duplicate`);
        set((state) => {
          state.projects = [copy, ...state.projects];
          // "Duplicate and open" — switch to the copy.
          state.currentProject = copy as unknown as Project;
          state.fileTree = null;
          state.openFiles = [];
          state.activeFileId = null;
          state.error = null;
        });
        writeActiveProjectId(copy.id);
        await get().fetchTree(copy.id);
        return { ok: true };
      } catch (e) {
        const msg = e instanceof ApiError ? e.message : String(e);
        return { ok: false, error: msg };
      }
    },

    archiveProject: async (projectId) => {
      try {
        await api.put(`/projects/${projectId}`, { archived: true });
      } catch (e) {
        const msg = e instanceof ApiError ? e.message : String(e);
        return { ok: false, error: msg };
      }
      // Archived projects drop out of the default list (backend filters them),
      // so remove locally + switch current if needed — same shape as
      // deleteProject, except the project stays on disk (restorable).
      const wasCurrent = get().currentProject?.id === projectId;
      const remaining = get().projects.filter((p) => p.id !== projectId);
      const next = wasCurrent ? (remaining[0] ?? null) : null;
      set((state) => {
        state.projects = remaining;
        if (wasCurrent) {
          state.currentProject = next ? (next as unknown as Project) : null;
          state.fileTree = null;
          state.openFiles = [];
          state.activeFileId = null;
        }
        state.error = null;
      });
      if (wasCurrent) {
        writeActiveProjectId(next?.id ?? null);
        if (next) await get().fetchTree(next.id);
      }
      return { ok: true };
    },

    unarchiveProject: async (projectId) => {
      // Unfreeze — restore a frozen (IceBox) project to the active list. The
      // Projects module reloads both lists after, so no local surgery beyond
      // clearing any error. Reversible + non-destructive → no confirm.
      try {
        await api.put(`/projects/${projectId}`, { archived: false });
      } catch (e) {
        const msg = e instanceof ApiError ? e.message : String(e);
        return { ok: false, error: msg };
      }
      set((state) => {
        state.error = null;
      });
      return { ok: true };
    },

    purgeProject: async (projectId) => {
      // Delete permanently (IceBox only) — DELETE ?purge=true hard-deletes the
      // Forgejo repo (not archive) + rmtrees the FS. Irreversible; the caller
      // gates it behind a two-step confirm. Distinct from deleteProject (which
      // ARCHIVES the repo). Remove from the active list too in case it is
      // somehow present (defensive; IceBox items are archived, not in it).
      try {
        await api.delete(`/projects/${projectId}?purge=true`); // 204
      } catch (e) {
        const msg = e instanceof ApiError ? e.message : String(e);
        return { ok: false, error: msg };
      }
      set((state) => {
        state.projects = state.projects.filter((p) => p.id !== projectId);
        state.error = null;
      });
      return { ok: true };
    },

    saveFile: async (fileId) => {
      // Phase A: persist editor contents through the existing
      // PUT /api/projects/{id}/files/{path} backend route. On success we
      // clear the dirty flag so the file-tab indicator (EditorPanel.tsx
      // EditorTabs `Circle` at line 37-39) goes away. On failure we leave
      // isModified=true so the user is NOT misled into thinking the edit
      // persisted — same no-silent-failure discipline as E.9's swallow fix.
      //
      // Phase B (E.21.37): dispatch lifecycle CustomEvents on `window` so
      // the useAutoRebuildOnSave hook can drive toasts + auto-rebuild
      // without coupling projectStore to buildStore. Mirrors EditorPanel
      // .tsx:111 `vibe:ai:explanation` pattern. Event detail carries the
      // identifying fields the listener needs.
      const state = get();
      const project = state.currentProject;
      const file = state.openFiles.find((f) => f.id === fileId);
      if (!project || !file) return;

      // 2026-08-17 empty-editor fix (Leg 2): a buffer whose content never
      // actually loaded must NEVER be written back — before this guard,
      // saving it overwrote the real file with emptiness and committed the
      // wipe to Forgejo. Guard sits BEFORE the save:start event so listeners
      // never see a phantom save lifecycle.
      if (file.contentSource === 'unfetched' || file.contentSource === 'error') {
        const msg = `Refusing to save ${file.name}: its content was not loaded into the editor.`;
        set((s) => {
          s.error = msg;
        });
        window.dispatchEvent(
          new CustomEvent('vibe:file:save:error', {
            detail: { projectId: project.id, fileId, path: file.path, error: msg },
          }),
        );
        return;
      }

      window.dispatchEvent(
        new CustomEvent('vibe:file:save:start', {
          detail: { projectId: project.id, fileId, path: file.path },
        }),
      );

      try {
        await api.put(`/projects/${project.id}/files${file.path}`, {
          content: file.content,
        });
        get().markFileSaved(fileId);
        set((s) => {
          s.error = null;
        });
        window.dispatchEvent(
          new CustomEvent('vibe:file:saved', {
            detail: { projectId: project.id, fileId, path: file.path },
          }),
        );
      } catch (e) {
        const msg = e instanceof ApiError ? e.message : String(e);
        set((s) => {
          s.error = msg;
        });
        window.dispatchEvent(
          new CustomEvent('vibe:file:save:error', {
            detail: { projectId: project.id, fileId, path: file.path, error: msg },
          }),
        );
      }
    },

    updateCursorPosition: (fileId, line, column) =>
      set((state) => {
        const file = state.openFiles.find((f) => f.id === fileId);
        if (file) {
          file.cursorPosition = { line, column };
        }
      }),

    // Getters
    getActiveFile: () => {
      const state = get();
      return state.openFiles.find((f) => f.id === state.activeFileId);
    },

    getFileById: (fileId) => {
      return get().openFiles.find((f) => f.id === fileId);
    },

    hasUnsavedChanges: () => {
      return get().openFiles.some((f) => f.isModified);
    },
  }))
);
